// Shared building blocks for the Finance Analyst company dashboards
// (PetronasDashboard, TauraniDashboard): KPI tiles, chart cards, statement
// tables, formatting and the full-screen dialog shell. Each dashboard supplies
// its own data, units and tab views.

import React, { useState } from 'react';
import {
  Dialog,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Tabs,
  Tab,
  Card,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip as MuiTooltip,
  alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Tooltip } from 'recharts';

// Categorical series colours in fixed order, stepped separately for light and
// dark surfaces (validated for colour-vision deficiency). An entity always takes
// the same slot so a colour means the same thing on every chart.
export const SERIES = {
  light: ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4'],
  dark: ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181'],
};

export const TONE = { positive: '#1baf7a', negative: '#e5484d', warning: '#eda100', neutral: '#2a78d6' };

export const RADIUS = { panel: '12px' };

// ---------- formatting ----------

export const isNum = (v) => v !== null && v !== undefined && !isNaN(v);

/**
 * Money formatters for one dashboard. `units` maps a toggle key to
 * { label, factor, digits, suffix }; `currency` is the ISO code shown before
 * every amount (e.g. "MYR 266.1 Bn").
 */
export const makeFormatters = (units, currency) => {
  const scaled = (v, unit) => {
    const u = units[unit];
    return Math.abs(v * u.factor).toLocaleString('en-GB', {
      minimumFractionDigits: u.digits,
      maximumFractionDigits: u.digits,
    });
  };
  return {
    fmtAmt: (v, unit) => {
      if (!isNum(v)) return '—';
      const suffix = units[unit].suffix;
      return `${v < 0 ? '-' : ''}${currency} ${scaled(v, unit)}${suffix ? ` ${suffix}` : ''}`;
    },
    fmtAxis: (v, unit) =>
      isNum(v) ? (v * units[unit].factor).toLocaleString('en-GB', { maximumFractionDigits: 1 }) : '',
    /** Table figure in the selected unit, negatives in accounting parentheses. */
    fmtCell: (v, unit) => {
      if (!isNum(v)) return '—';
      const s = scaled(v, unit);
      return v < 0 ? `(${s})` : s;
    },
  };
};

/** Statement-table figure as printed, negatives in accounting parentheses. */
export const fmtStatement = (v) => {
  if (!isNum(v) || v === 0) return '—';
  const s = Math.abs(v).toLocaleString('en-GB');
  return v < 0 ? `(${s})` : s;
};

export const fmtPct = (v, digits = 1) => (isNum(v) ? `${v.toFixed(digits)}%` : '—');
export const fmtX = (v) => (isNum(v) ? `${v.toFixed(2)}x` : '—');

export const growth = (cur, prv) =>
  isNum(cur) && isNum(prv) && prv !== 0 && (cur < 0) === (prv < 0)
    ? ((cur - prv) / Math.abs(prv)) * 100
    : null;

export const deltaProps = (cur, prv, goodUp = true) => {
  const g = growth(cur, prv);
  if (g === null) return {};
  return {
    delta: `${g > 0 ? '▲' : '▼'} ${Math.abs(g).toFixed(1)}%`,
    deltaTone: (g >= 0) === goodUp ? 'positive' : 'negative',
  };
};

// ---------- building blocks ----------

export const useSeries = () => {
  const theme = useTheme();
  return SERIES[theme.palette.mode === 'dark' ? 'dark' : 'light'];
};

export const Stat = ({ label, value, sub, delta, deltaTone, tone = 'neutral', hero = false, title }) => {
  const theme = useTheme();
  const accent = TONE[tone] || TONE.neutral;
  const body = (
    <Card
      elevation={0}
      sx={{
        flex: hero ? '1 1 210px' : '1 1 155px',
        minWidth: hero ? 190 : 145,
        maxWidth: hero ? 360 : 300,
        p: hero ? 2.25 : 1.75,
        borderRadius: RADIUS.panel,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.paper,
        position: 'relative',
        overflow: 'hidden',
        '&::before': { content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, bgcolor: accent },
      }}
    >
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 0.6,
          textTransform: 'uppercase',
          color: theme.palette.text.secondary,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: hero ? 26 : 19, fontWeight: 700, lineHeight: 1.15, color: theme.palette.text.primary }}>
        {value}
      </Typography>
      {(sub || delta) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.4, flexWrap: 'wrap' }}>
          {delta && (
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: TONE[deltaTone] || theme.palette.text.secondary }}>
              {delta}
            </Typography>
          )}
          {sub && <Typography sx={{ fontSize: 11.5, color: theme.palette.text.secondary }}>{sub}</Typography>}
        </Box>
      )}
    </Card>
  );
  return title ? (
    <MuiTooltip title={title} arrow>
      {body}
    </MuiTooltip>
  ) : (
    body
  );
};

export const Section = ({ title, caption, children }) => {
  const theme = useTheme();
  return (
    <Box sx={{ mb: 3.5 }}>
      {title && (
        <Box sx={{ mb: 1.25 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase' }}>
            {title}
          </Typography>
          {caption && (
            <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary, mt: 0.25 }}>{caption}</Typography>
          )}
        </Box>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>{children}</Box>
    </Box>
  );
};

export const ChartCard = ({ title, caption, children, height = 290, full = false }) => {
  const theme = useTheme();
  return (
    <Card
      elevation={0}
      sx={{
        flex: full ? '1 1 100%' : '1 1 440px',
        minWidth: 280,
        p: 2,
        borderRadius: RADIUS.panel,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.paper,
      }}
    >
      <Typography sx={{ fontSize: 14, fontWeight: 700, mb: caption ? 0.25 : 1.5 }}>{title}</Typography>
      {caption && (
        <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary, mb: 1.25 }}>{caption}</Typography>
      )}
      <Box sx={{ width: '100%', height }}>{children}</Box>
    </Card>
  );
};

export const Callout = ({ children, tone = 'warning' }) => {
  const theme = useTheme();
  const accent = TONE[tone];
  return (
    <Box
      sx={{
        mb: 2,
        p: 1.5,
        borderRadius: RADIUS.panel,
        border: `1px solid ${alpha(accent, 0.5)}`,
        bgcolor: alpha(accent, 0.08),
        display: 'flex',
        gap: 1,
        alignItems: 'flex-start',
      }}
    >
      <InfoOutlinedIcon sx={{ fontSize: 18, color: accent, mt: 0.2 }} />
      <Box sx={{ fontSize: 12.5, color: theme.palette.text.secondary, lineHeight: 1.6 }}>{children}</Box>
    </Box>
  );
};

export const useAxis = () => {
  const theme = useTheme();
  return {
    tick: { fill: theme.palette.text.secondary, fontSize: 11 },
    stroke: alpha(theme.palette.text.secondary, 0.3),
    tickLine: false,
  };
};

export const ChartTip = ({ formatter }) => {
  const theme = useTheme();
  return (
    <Tooltip
      formatter={formatter}
      contentStyle={{
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        fontSize: 12,
        color: theme.palette.text.primary,
      }}
      cursor={{ fill: alpha(theme.palette.text.secondary, 0.08) }}
    />
  );
};

export const legendStyle = { fontSize: 11 };
export const BAR_END = [4, 4, 0, 0];

export const StatementTable = ({ title, caption, periods, layout, values, unitLabel }) => {
  const theme = useTheme();
  return (
    <Card
      elevation={0}
      sx={{ flex: '1 1 100%', p: 2, borderRadius: RADIUS.panel, border: `1px solid ${theme.palette.divider}`, overflowX: 'auto' }}
    >
      <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{title}</Typography>
      <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary, mb: 1.25 }}>{caption}</Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, fontSize: 12, color: theme.palette.text.secondary }}>{unitLabel}</TableCell>
            {periods.map((p) => (
              <TableCell key={p} align="right" sx={{ fontWeight: 700, fontSize: 12, color: theme.palette.text.secondary }}>
                {p}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {layout.map(([key, label, total]) => (
            <TableRow key={key} sx={total ? { bgcolor: alpha(theme.palette.text.primary, 0.04) } : undefined}>
              <TableCell sx={{ fontSize: 12.5, fontWeight: total ? 700 : 400, pl: total ? 2 : 3.5 }}>{label}</TableCell>
              {values[key].map((v, i) => (
                <TableCell
                  key={i}
                  align="right"
                  sx={{ fontSize: 12.5, fontWeight: total ? 700 : 400, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                >
                  {fmtStatement(v)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
};

// ---------- shell ----------

/**
 * Full-screen dashboard dialog: title bar, unit toggle and tabs. `views` maps a
 * tab key to a component that receives { a: analytics, unit }.
 */
export const DashboardShell = ({ open, onClose, title, subtitle, tabs, views, units, defaultUnit, analytics }) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(tabs[0].key);
  const [unit, setUnit] = useState(defaultUnit);
  const ActiveView = views[activeTab];

  return (
    <Dialog fullScreen open={open} onClose={onClose} PaperProps={{ sx: { bgcolor: theme.palette.background.default } }}>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: { xs: 52, sm: 60 }, gap: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: 17, fontWeight: 700, lineHeight: 1.2 }}>
              {title}
            </Typography>
            <Typography noWrap sx={{ fontSize: 11.5, color: theme.palette.text.secondary }}>
              {subtitle}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={unit}
              onChange={(_, v) => v && setUnit(v)}
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontSize: 12,
                  fontWeight: 600,
                  px: 1.25,
                  py: 0.25,
                  borderRadius: '999px !important',
                  border: `1px solid ${theme.palette.divider} !important`,
                  color: theme.palette.text.secondary,
                },
                '& .Mui-selected': { color: theme.palette.text.primary },
              }}
            >
              {Object.entries(units).map(([key, u]) => (
                <ToggleButton key={key} value={key}>
                  {u.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <IconButton onClick={onClose} sx={{ color: theme.palette.text.primary }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Toolbar>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: 2, borderTop: `1px solid ${theme.palette.divider}`, minHeight: 44 }}
        >
          {tabs.map((t) => (
            <Tab key={t.key} value={t.key} label={t.label} sx={{ fontWeight: 600, textTransform: 'none', fontSize: 13.5, minHeight: 44 }} />
          ))}
        </Tabs>
      </AppBar>

      <Box sx={{ p: { xs: 1.5, sm: 2.5 }, overflowY: 'auto' }}>
        <ActiveView a={analytics} unit={unit} />
      </Box>
    </Dialog>
  );
};
